import { inject, Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import { ClassroomPresenceActions } from "./state/classroom-presence.actions";
import * as fromClassroomPresence from "./state/classroom-presence.selectors";
import { groupClientsByUserId } from "./util/group-clients";
import { ClassroomPresenceUser, OfflineState } from "./models/classroom-presence-user";
import { ClientType } from "@pcp/core/interfaces";

const filterByClientType = (clientType: ClientType) => (users: (ClassroomPresenceUser & OfflineState)[]) =>
    users.filter(user => user.clientType === clientType);

@Injectable()
export class ClassroomPresenceFacade extends ComponentStore<Record<string, never>> {

    private readonly store = inject(Store);

    readonly classroomPresence$ = this.store.select(fromClassroomPresence.selectClassroomPresence);

    readonly teachers$ = this.select(this.classroomPresence$, filterByClientType('Teacher'));
    readonly students$ = this.select(this.classroomPresence$, filterByClientType('Student'));
    readonly beamers$ = this.select(this.classroomPresence$, filterByClientType('Beamer'));
    readonly moderators$ = this.select(this.classroomPresence$, filterByClientType('LiveLessonModerator'));

    readonly teachersGrouped$ = this.select(this.teachers$, groupClientsByUserId);
    readonly studentsGrouped$ = this.select(this.students$, groupClientsByUserId);
    readonly beamersGrouped$ = this.select(this.beamers$, groupClientsByUserId);
    readonly moderatorsGrouped$ = this.select(this.moderators$, groupClientsByUserId);

    /**
     * Load or refresh all users connected to the classroom.
     */
    loadClassroomPresence = () => this.store.dispatch(ClassroomPresenceActions.loadClassroomPresence());

    /**
     * Ping a single user in the classroom in order to check if he is still connected.
     */
    pingUser = (client: ClassroomPresenceUser) => this.store.dispatch(ClassroomPresenceActions.pingUser({ client }));
}
